import {
  coerceDateTime,
  destinationLabel,
  encodeDestinationSummary,
  parseDestinationSummary,
} from '../my-journeys/myJourneysModel';

const pad2 = (value) => String(value).padStart(2, '0');

export function snowflakeId(value) {
  if (value == null || value === '') return null;
  return String(value);
}

/** Nested Accommodation.location — ids stay string; coords stay numeric-parseable. */
export function normalizeNestedLocation(value) {
  if (!value || typeof value !== 'object') return null;
  const id = snowflakeId(value.id);
  if (!id && value.name == null && value.address == null
      && value.latitude == null && value.longitude == null) {
    return null;
  }
  return {
    id,
    name: value.name == null ? '' : String(value.name),
    address: value.address == null ? '' : String(value.address),
    latitude: value.latitude ?? null,
    longitude: value.longitude ?? null,
  };
}

/** Coerce LocalDateTime ISO/array into datetime-local value (YYYY-MM-DDTHH:mm). */
export function coerceDateTimeLocal(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}):(\d{2})/);
    return match ? `${match[1]}T${match[2]}:${match[3]}` : '';
  }
  if (Array.isArray(value) && value.length >= 5) {
    return `${value[0]}-${pad2(value[1])}-${pad2(value[2])}T${pad2(value[3])}:${pad2(value[4])}`;
  }
  if (Array.isArray(value) && value.length >= 3) {
    return `${value[0]}-${pad2(value[1])}-${pad2(value[2])}T00:00`;
  }
  return '';
}

/** Normalize datetime-local / ISO text for Travel LocalDateTime JSON. */
export function toApiDateTime(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(text)) return text.slice(0, 19);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text)) return `${text}:00`;
  return text;
}

export function encodeLocationSummary(text) {
  return encodeDestinationSummary(text);
}

export function locationLabelFromSummary(raw) {
  return destinationLabel(raw);
}

export function classifyLogisticsError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (code === 92001 || code === 93001 || key.includes('not.found')) return 'not-found';
  if (code === 92002 || code === 93002 || key.includes('permission.denied')) return 'forbidden';
  if (code === 92000 || code === 93000 || key.includes('validation')) return 'validation';
  return 'error';
}

export function normalizeAccommodationSummary(value) {
  return {
    accommodationId: snowflakeId(value?.accommodationId),
    propertyName: value?.propertyName || 'Untitled stay',
    locationId: snowflakeId(value?.locationId),
    location: normalizeNestedLocation(value?.location),
    // 清單若帶 locationSummaryJson，解析為可讀地點標籤
    locationLabel: locationLabelFromSummary(value?.locationSummaryJson),
    checkInAt: coerceDateTimeLocal(value?.checkInAt),
    checkOutAt: coerceDateTimeLocal(value?.checkOutAt),
    confirmationReference: value?.confirmationReference || '',
    checkInDisplay: coerceDateTime(value?.checkInAt),
    checkOutDisplay: coerceDateTime(value?.checkOutAt),
  };
}

export function normalizeAccommodationDetail(value) {
  return {
    ...normalizeAccommodationSummary(value),
    journeyId: snowflakeId(value?.journeyId),
    locationSummaryJson: value?.locationSummaryJson || null,
    locationLabel: locationLabelFromSummary(value?.locationSummaryJson),
    addressText: value?.addressText || '',
    note: value?.note || '',
    createdByMemberId: snowflakeId(value?.createdByMemberId),
    createTime: coerceDateTime(value?.createTime),
    updateTime: coerceDateTime(value?.updateTime),
  };
}

/** Picker selected node from Accommodation detail (preserves dangling raw locationId). */
export function locationNodeFromAccommodation(detail) {
  if (detail?.location?.id) {
    return {
      locationId: detail.location.id,
      displayName: detail.location.name || '已選地點',
      latitude: detail.location.latitude ?? null,
      longitude: detail.location.longitude ?? null,
      address: detail.location.address ?? null,
    };
  }
  if (detail?.locationId) {
    return {
      locationId: detail.locationId,
      displayName: detail.locationLabel || '已選地點',
    };
  }
  return null;
}

export function emptyAccommodationDraft() {
  return {
    propertyName: '',
    location: '',
    locationId: null,
    locationNode: null,
    addressText: '',
    checkInAt: '',
    checkOutAt: '',
    confirmationReference: '',
    note: '',
  };
}

export function draftFromAccommodationDetail(detail) {
  return {
    propertyName: detail?.propertyName || '',
    location: detail?.locationLabel || '',
    locationId: detail?.locationId ?? null,
    locationNode: locationNodeFromAccommodation(detail),
    addressText: detail?.addressText || '',
    checkInAt: detail?.checkInAt || '',
    checkOutAt: detail?.checkOutAt || '',
    confirmationReference: detail?.confirmationReference || '',
    note: detail?.note || '',
  };
}

/** Apply picker selection; clear → locationId null. Does not wipe free-text fields. */
export function applyAccommodationLocationSelection(draft, node) {
  if (!node || node.locationId == null || node.locationId === '') {
    return {
      ...draft,
      locationId: null,
      locationNode: null,
    };
  }
  return {
    ...draft,
    locationId: snowflakeId(node.locationId),
    locationNode: {
      locationId: snowflakeId(node.locationId),
      displayName: node.displayName || '已選地點',
      latitude: node.latitude ?? null,
      longitude: node.longitude ?? null,
      address: node.address ?? null,
    },
  };
}

/** Full-replace Accommodation write body — all mutable fields every submit. */
export function buildAccommodationWritePayload(draft) {
  return {
    propertyName: String(draft?.propertyName || '').trim(),
    locationId: draft?.locationId == null || draft.locationId === ''
      ? null
      : String(draft.locationId),
    locationSummaryJson: encodeLocationSummary(draft?.location),
    addressText: String(draft?.addressText || '').trim() || null,
    checkInAt: toApiDateTime(draft?.checkInAt),
    checkOutAt: toApiDateTime(draft?.checkOutAt),
    confirmationReference: String(draft?.confirmationReference || '').trim() || null,
    note: String(draft?.note || '').trim() || null,
  };
}

export function validateAccommodationDraft(draft) {
  if (!String(draft?.propertyName || '').trim()) return 'Property name is required.';
  const checkIn = toApiDateTime(draft?.checkInAt);
  const checkOut = toApiDateTime(draft?.checkOutAt);
  if (checkIn && checkOut && checkOut < checkIn) {
    return 'Check-out must be on or after check-in.';
  }
  return '';
}

export { parseDestinationSummary };
